import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, HostListener, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { COMPANY_MENU_SHORTCUTS, CompanyMenuShortcut } from '../../_services/company-menu-shortcuts';
import { MenuEdMenu, MenuEdNodo, MenuEdVoce } from '../../_services/menu-editor-model';
import { MenuEditService, MenuEdPosizione, MenuEdRigaMappa } from '../../_services/menu-edit.service';

/** Dove cadrebbe la voce trascinata. */
type Bersaglio = { tipo: 'barra'; index: number; i: number; prima: boolean } | { tipo: 'dentro'; menu: string } | { tipo: 'riga'; menu: string; index: number; j: number; prima: boolean } | { tipo: 'cestino' };

/** Punto in cui inserire, scelto col "+" tra due voci. */
interface Inseritore extends MenuEdPosizione {
	top: number;
	left: number;
	scelta: 'pagina' | 'collegamento' | null;
}

/** Tutte le icone PrimeIcons ('pi pi-...'), in ordine di nome, per la scelta dell'icona delle voci. */
const ICONE_MENU: string[] = Array.from(new Set(Object.values(PrimeIcons) as string[])).sort();

/** Larghezza della sidebar mentre la si modifica (normalmente --kcs-menu-width = 11rem: i nomi non ci stavano). */
const LARGHEZZA_SIDEBAR_IN_MODIFICA = '16rem';

/**
 * Menu del portale in modifica, al posto del menu vero: barra in alto (verticale = sidebar).
 * Tre livelli come in Word: barra, menu che si apre sotto, sottomenu che si apre di lato.
 * Trascinamento fatto a mano con pointer events: il bersaglio si calcola dal punto sotto il
 * puntatore (elementFromPoint) senza spostare le altre voci, quindi funziona anche sui menu
 * aperti durante il trascinamento (il CDK fissa le liste e le loro misure all'avvio).
 * Per inserire in un punto preciso c'e' anche il "+" tra due voci.
 */
@Component({
	selector: 'app-menu-editor',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgTemplateOutlet, FormsModule, TranslatePipe, ButtonModule, DialogModule, SelectModule, ToggleSwitchModule, TooltipModule],
	templateUrl: './app-menu-editor.component.html',
	styleUrl: './app-menu-editor.component.css',
})
export class AppMenuEditorComponent implements AfterViewInit, OnDestroy {
	/** true nella sidebar (menu a sinistra): menu e sottomenu si aprono sotto la voce */
	@Input() verticale = false;
	/** nella sidebar l'editor prende il posto di nav.sidebar-nav: scorre lui */
	@HostBinding('class.med-host-v') get hostVerticale() {
		return this.verticale;
	}
	@ViewChild('barra') barra?: ElementRef<HTMLElement>;

	readonly icone = ICONE_MENU;
	/** Menu aperto nella barra in alto e suo sottomenu aperto di lato (uno alla volta) */
	aperto: string | null = null;
	apertoSub: string | null = null;
	/** Menu e sottomenu aperti nella sidebar */
	apertiV = new Set<string>();
	posTendina = { top: 0, left: 0 };
	posSub = { top: 0, left: 0 };
	/** Scelta dell'icona aperta (voce e posizione del riquadro) */
	iconeDi: { uid: string; top: number; left: number } | null = null;
	cercaIcona = '';

	rinominaUid: string | null = null;
	rinominaValore = '';
	private rinominaIniziale = '';

	paletteAperta = false;
	cercaCollegamento = '';

	/** Mappa del menu (vista ad albero per il ruolo di "Vedi come", stampabile) */
	mappaAperta = false;
	righeMappa: MenuEdRigaMappa[] = [];
	contaMappa = { menu: 0, pagine: 0, collegamenti: 0 };

	inseritore: Inseritore | null = null;
	/** Voci cercabili in "Pagina": nome, dove sta, indirizzo (la ricerca usa nome + indirizzo) */
	opzioniPagine: { label: string; value: string; url: string; cerca: string }[] = [];
	opzioniCollegamenti: { label: string; items: { label: string; value: string }[] }[] = [];

	bersaglio: Bersaglio | null = null;
	private chiaveBersaglio = '';
	private trascinamento: { uid?: string; nuovo?: string; x: number; y: number; attivo: boolean; ghost?: HTMLElement } | null = null;
	private ignoraClick = false;
	private timerApertura: any = null;
	private aperturaInAttesa = '';
	/** Scorrimento in corso (puntatore vicino al bordo della barra o di una lista lunga, anche da fermo) */
	private scorrimento: { el: HTMLElement; dx: number; dy: number } | null = null;
	private timerScorrimento: any = null;
	private puntatore = { x: 0, y: 0 };
	private suMove = (e: PointerEvent) => this.muovi(e);
	private suUp = (e: PointerEvent) => this.rilascia(e);

	constructor(
		public svc: MenuEditService,
		private translate: TranslateService,
		private zone: NgZone,
		private host: ElementRef<HTMLElement>,
	) {}

	ngAfterViewInit() {
		if (this.verticale) this.allargaSidebar();
	}

	ngOnDestroy() {
		this.fineTrascinamento();
		this.roStrumenti?.disconnect();
		this.ripristinaSidebar?.();
	}

	private ripristinaSidebar?: () => void;

	/**
	 * Sidebar piu' larga (anche se minimizzata) finche' l'editor e' aperto, col contenuto spostato di conseguenza;
	 * all'uscita torna com'era. Stili in linea: battono anche la regola di body.sidebar-minimized.
	 */
	private allargaSidebar() {
		const html = document.documentElement;
		const sidebar = this.host.nativeElement.closest<HTMLElement>('.sidebar');
		const corpo = document.querySelector<HTMLElement>('.app-body');
		html.style.setProperty('--kcs-menu-width', LARGHEZZA_SIDEBAR_IN_MODIFICA);
		sidebar?.style.setProperty('width', 'var(--kcs-menu-width)');
		corpo?.style.setProperty('padding-left', 'var(--kcs-menu-width)');
		this.ripristinaSidebar = () => {
			html.style.removeProperty('--kcs-menu-width');
			sidebar?.style.removeProperty('width');
			corpo?.style.removeProperty('padding-left');
		};
	}

	readonly aiuto = "Trascina per spostare · + tra due voci per inserire una pagina, un gruppo o un collegamento · doppio clic per rinominare · clic sull'icona per cambiarla";

	/** Altezza della barra degli strumenti: i pannelli (impostazioni, collegamenti, avvisi) si aprono sopra (--med-tb). */
	@ViewChild('strumenti') set strumentiEl(el: ElementRef<HTMLElement> | undefined) {
		this.roStrumenti?.disconnect();
		if (!el) return;
		this.roStrumenti = new ResizeObserver(() => this.host.nativeElement.style.setProperty('--med-tb', el.nativeElement.offsetHeight + 28 + 'px'));
		this.roStrumenti.observe(el.nativeElement);
	}
	private roStrumenti?: ResizeObserver;

	get opzioniRuolo() {
		return [{ label: this.translate.instant('Tutti i ruoli'), value: null }, ...this.svc.ruoli.map((r) => ({ label: r.rl_name, value: r.rl_id }))];
	}

	/** Menu in alto con lo stile barra come Word: il gruppo aperto si vede come la barra, sottogruppi affiancati */
	get ribbon(): boolean {
		return !this.verticale && this.svc.stile === 'barra';
	}

	/**
	 * Barra come Word con i sottogruppi a blocchi (cmp_menu_org.barraSottogruppi = 'blocchi'): nel pannello i sottogruppi
	 * restano aperti uno accanto all'altro. Con la tendina (default) sono voci con la freccia che aprono la loro lista
	 * sotto, come nel portale.
	 */
	get ribbonBlocchi(): boolean {
		return this.ribbon && this.svc.barraBlocchi;
	}

	readonly opzioniStile = [
		{ label: 'A tendina', value: 'tendina' },
		{ label: 'A barra, come Word', value: 'barra' },
	];
	readonly opzioniLayout = [
		{ label: 'A sinistra', value: 'left' },
		{ label: 'In alto', value: 'top' },
		{ label: 'In alto, voci centrate', value: 'top-center' },
	];
	readonly opzioniACapo = [
		{ label: 'Nel menu "Altro"', value: false },
		{ label: 'Vanno a capo', value: true },
	];
	readonly opzioniBarraBlocchi = [
		{ label: 'A tendina al passaggio del mouse', value: false },
		{ label: 'Blocchi con il nome sotto', value: true },
	];
	readonly opzioniBarraACapo = [
		{ label: 'Scorrono in orizzontale', value: false },
		{ label: 'Vanno a capo', value: true },
	];

	/**
	 * Pannello "Impostazioni menu": menu dell'azienda, attivazione, posizione, stile, esporta/importa.
	 * Lo stato sta nel servizio: cambiando posizione l'editor passa tra barra in alto e sidebar (altra istanza).
	 */
	get impostazioniAperte(): boolean {
		return this.svc.pannelloImpostazioni;
	}
	set impostazioniAperte(v: boolean) {
		this.svc.pannelloImpostazioni = v;
	}

	/** "Elimina menu" premuto: chiede conferma nel pannello */
	confermaElimina = false;

	/** Menu salvati dell'azienda per il selettore; un menu nuovo non ancora salvato compare in fondo. */
	get opzioniProfili(): { label: string; value: string }[] {
		const t = (k: string) => this.translate.instant(k);
		const out = this.svc.store.profiles.map((p) => ({ label: p.id === this.svc.store.activeId ? `${p.name} (${t('attivo')})` : p.name, value: p.id }));
		if (!this.svc.profiloId) out.push({ label: `${this.svc.nomeProfilo} (${t('da salvare')})`, value: '' });
		return out;
	}

	onImporta(ev: Event) {
		const input = ev.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (file) this.svc.importa(file);
	}

	get inTrascinamentoAttivo(): boolean {
		return !!this.trascinamento?.attivo;
	}

	private gruppo(uid: string | null): MenuEdMenu | null {
		const n = uid ? this.svc.trova(uid)?.nodo : null;
		return n?.tipo === 'menu' ? n : null;
	}

	get menuAperto(): MenuEdMenu | null {
		return this.gruppo(this.aperto);
	}

	get subAperto(): MenuEdMenu | null {
		return this.gruppo(this.apertoSub);
	}

	isAperto(n: MenuEdNodo): boolean {
		return this.verticale ? this.apertiV.has(n.uid) : this.aperto === n.uid || this.apertoSub === n.uid;
	}

	/** Che cosa e' la voce: una pagina vera del portale, un collegamento o un gruppo virtuale (menu, sottomenu). */
	tipo(n: MenuEdNodo, livello: number): { icon: string; testo: string } {
		if (n.tipo === 'menu' && livello === 0) return { icon: 'pi pi-folder-open', testo: "Gruppo: una voce della barra che raccoglie altre voci, non e' una pagina" };
		if (n.tipo === 'menu') return { icon: 'pi pi-sitemap', testo: "Sottogruppo: un gruppo dentro il gruppo (di lato nella tendina, un blocco nella barra come Word), non e' una pagina" };
		if (n.tipo === 'collegamento') return { icon: 'pi pi-link', testo: 'Collegamento a una sezione di una pagina (come le card)' };
		return { icon: 'pi pi-file', testo: 'Pagina del portale' };
	}

	// ---- palette dei collegamenti ----

	/** Collegamenti per gruppo (pagina delle card); `cerca` filtra per nome (la palette usa la sua ricerca). */
	gruppiCollegamenti(cerca = this.cercaCollegamento): { gruppo: string; voci: CompanyMenuShortcut[] }[] {
		const testo = cerca.trim().toLowerCase();
		const gruppi = new Map<string, CompanyMenuShortcut[]>();
		for (const sc of COMPANY_MENU_SHORTCUTS) {
			if (sc.soloRfi && !this.svc.rfiAttivo) continue;
			if (testo && !(this.translate.instant(sc.labelKey) + ' ' + sc.gruppo).toLowerCase().includes(testo)) continue;
			const lista = gruppi.get(sc.gruppo) || [];
			lista.push(sc);
			gruppi.set(sc.gruppo, lista);
		}
		return Array.from(gruppi, ([gruppo, voci]) => ({ gruppo, voci }));
	}

	/** Punto in cui va una voce cliccata nella palette: in fondo al (sotto)gruppo aperto, altrimenti in fondo alla barra. */
	private destPalette(): MenuEdPosizione {
		const aperti = this.verticale ? this.svc.albero.filter((n) => this.apertiV.has(n.uid)) : [];
		const menu = this.verticale ? (aperti[0] as MenuEdMenu | undefined) : this.subAperto || this.menuAperto;
		return menu?.tipo === 'menu' ? { menu: menu.uid, index: menu.voci.length } : { menu: null, index: this.svc.albero.length };
	}

	aggiungiDaPalette(sc: CompanyMenuShortcut) {
		if (this.ignoraClick) return;
		this.svc.aggiungiCollegamento(sc.key, this.destPalette());
	}

	/** Clic su una pagina nascosta della palette: torna nel menu. */
	ripristinaDaPalette(pg_id: number) {
		if (this.ignoraClick) return;
		this.svc.ripristina(pg_id, this.destPalette());
	}

	// ---- mappa del menu ----

	apriMappa() {
		this.righeMappa = this.svc.mappa();
		const conta = { menu: 0, pagine: 0, collegamenti: 0 };
		const visita = (righe: MenuEdRigaMappa[]) =>
			righe.forEach((r) => {
				if (r.tipo === 'pagina') conta.pagine++;
				else if (r.tipo === 'collegamento') conta.collegamenti++;
				else conta.menu++;
				visita(r.figli);
			});
		visita(this.righeMappa);
		this.contaMappa = conta;
		this.mappaAperta = true;
	}

	tipoMappa(r: MenuEdRigaMappa): string {
		return { menu: 'Gruppo', sottomenu: 'Sottogruppo', pagina: 'Pagina', collegamento: 'Collegamento' }[r.tipo];
	}

	/** Stampa la mappa da un iframe nascosto (niente finestra da sbloccare): intestazione con azienda, ruolo e data. */
	stampaMappa() {
		const t = (k: string) => this.translate.instant(k);
		const esc = (v: string) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
		const righe = (lista: MenuEdRigaMappa[]): string =>
			lista.length
				? '<ul>' +
					lista
						.map((r) => {
							const gruppo = r.tipo === 'menu' || r.tipo === 'sottomenu';
							const nota = r.scheda ? ` <em>(${esc(t('nelle schede di'))} ${esc(t(r.scheda))})</em>` : '';
							const url = r.url ? ` <span class="url">${esc(r.url)}</span>` : '';
							return `<li class="${r.tipo}"><span class="tipo">${esc(t(this.tipoMappa(r)))}</span> <span class="${gruppo ? 'gruppo' : ''}">${esc(t(r.titolo))}</span>${url}${nota}${righe(r.figli)}</li>`;
						})
						.join('') +
					'</ul>'
				: '';
		const azienda = localStorage.getItem('def_company_name') || '';
		const ruolo = this.svc.nomeRuolo() || t('Tutti i ruoli');
		const c = this.contaMappa;
		const bozza = this.svc.modificato ? ' · ' + esc(t('bozza non ancora salvata')) : '';
		const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(t('Mappa del menu'))}</title><style>
			body { font: 12px/1.45 Arial, sans-serif; color: #222; margin: 18mm 14mm; }
			h1 { font-size: 17px; margin: 0 0 4px; }
			.info { color: #555; margin-bottom: 14px; }
			ul { list-style: none; margin: 0; padding-left: 18px; border-left: 1px solid #bbb; }
			body > ul { padding-left: 0; border-left: 0; }
			li { margin: 3px 0; break-inside: avoid; }
			li.menu { margin-top: 10px; }
			.gruppo { font-weight: bold; }
			.tipo { display: inline-block; min-width: 76px; font-size: 9px; text-transform: uppercase; color: #666; border: 1px solid #ccc; border-radius: 3px; padding: 0 4px; text-align: center; }
			.url { color: #777; font-size: 10px; margin-left: 6px; }
			em { color: #8a5a00; font-size: 10px; }
		</style></head><body>
			<h1>${esc(t('Mappa del menu'))}${azienda ? ' — ' + esc(azienda) : ''}</h1>
			<div class="info">${esc(t('Ruolo'))}: <strong>${esc(ruolo)}</strong> · ${esc(new Date().toLocaleString('it-IT'))} · ${c.menu} ${esc(t('gruppi e sottogruppi'))}, ${c.pagine} ${esc(t('pagine'))}, ${c.collegamenti} ${esc(t('collegamenti'))}${bozza}</div>
			${righe(this.righeMappa)}
		</body></html>`;
		const frame = document.createElement('iframe');
		Object.assign(frame.style, { position: 'fixed', width: '0', height: '0', border: '0', right: '0', bottom: '0' });
		document.body.appendChild(frame);
		const doc = frame.contentDocument!;
		doc.open();
		doc.write(html);
		doc.close();
		setTimeout(() => {
			frame.contentWindow?.focus();
			frame.contentWindow?.print();
			setTimeout(() => frame.remove(), 1000);
		}, 100);
	}

	// ---- inserimento in un punto preciso ("+" tra due voci) ----

	apriInseritore(menu: string | null, index: number, ev: Event) {
		ev.stopPropagation();
		const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
		const alto = 300;
		const top = r.bottom + 6 + alto > window.innerHeight ? Math.max(8, r.top - 6 - alto) : r.bottom + 6;
		this.inseritore = { menu, index, top, left: Math.max(8, Math.min(r.left - 16, window.innerWidth - 316)), scelta: null };
		const dove = (percorso: string[]) => (percorso.length ? percorso.map((p) => this.translate.instant(p)).join(' › ') : this.translate.instant('barra'));
		const usati = this.svc.collegamentiUsati();
		const cmp = this.svc.cmpAttiva;
		const opzione = (label: string, value: string, url: string) => ({ label, value, url, cerca: `${label} ${url}` });
		this.opzioniPagine = [
			// pagine e collegamenti gia' nel menu: si spostano qui (value = uid)
			...this.svc.elencoVoci().map((p) => opzione(`${this.translate.instant(this.svc.titolo(p.nodo))} — ${dove(p.percorso)}`, p.uid, this.svc.urlDi(p.nodo))),
			// le pagine tolte dal menu si rimettono da qui (value 'pg:<id>')
			...this.svc.pagineNascoste().map((p) => opzione(`${this.translate.instant(p.titolo)} — ${this.translate.instant('nascosta')}`, 'pg:' + p.pg_id, p.url)),
			// collegamenti non ancora nel menu, cruscotti compresi (value 'sc:<chiave>')
			...COMPANY_MENU_SHORTCUTS.filter((sc) => !usati.has(sc.key) && (!sc.soloRfi || this.svc.rfiAttivo)).map((sc) => opzione(`${this.translate.instant(sc.labelKey)} — ${this.translate.instant(sc.hub ? 'cruscotto' : 'da aggiungere')}`, 'sc:' + sc.key, sc.url(cmp))),
		].sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
		this.opzioniCollegamenti = this.gruppiCollegamenti('')
			.map((g) => ({ label: this.translate.instant(g.gruppo), items: g.voci.filter((sc) => !usati.has(sc.key)).map((sc) => ({ label: this.translate.instant(sc.labelKey), value: sc.key })) }))
			.filter((g) => g.items.length);
	}

	chiudiInseritore() {
		this.inseritore = null;
	}

	/** Nome del punto di inserimento: la barra o il (sotto)menu. */
	get doveInserisco(): string {
		const g = this.gruppo(this.inseritore?.menu ?? null);
		return g ? g.label : 'barra';
	}

	inserisciGruppo() {
		const ins = this.inseritore;
		if (!ins) return;
		const g = this.svc.nuovoGruppo(ins);
		this.inseritore = null;
		if (!g) return;
		if (ins.menu == null) this.apri(g.uid);
		else this.apriSub(g.uid);
		this.avviaRinomina(g);
	}

	inserisciPagina(uid: string) {
		const ins = this.inseritore;
		if (ins && uid?.startsWith('pg:')) this.svc.ripristina(Number(uid.slice(3)), ins);
		else if (ins && uid?.startsWith('sc:')) this.svc.aggiungiCollegamento(uid.slice(3), ins);
		else if (ins && uid) this.svc.sposta(uid, ins);
		this.inseritore = null;
		this.riposiziona();
	}

	inserisciCollegamento(key: string) {
		const ins = this.inseritore;
		if (ins && key) this.svc.aggiungiCollegamento(key, ins);
		this.inseritore = null;
		this.riposiziona();
	}

	@HostListener('document:pointerdown', ['$event'])
	fuori(ev: PointerEvent) {
		const el = ev.target as HTMLElement;
		if (this.inseritore && !el.closest?.('.med-ins-pop, .p-select-overlay, .med-piu')) this.inseritore = null;
		if (this.iconeDi && !el.closest?.('.med-icone-pop, .med-ico, .med-icona')) this.iconeDi = null;
	}

	// ---- menu, nomi, icone ----

	clicVoce(n: MenuEdNodo, ev: MouseEvent) {
		if (this.ignoraClick || this.rinominaUid === n.uid) return;
		ev.stopPropagation();
		if (n.tipo === 'menu') this.apriChiudi(n.uid);
	}

	/** Clic su una riga di un menu: un sottomenu si apre/chiude, le voci non fanno nulla (niente navigazione in modifica). */
	clicRiga(v: MenuEdNodo, ev: MouseEvent) {
		ev.stopPropagation();
		// nella barra come Word a blocchi i sottogruppi sono sempre aperti (affiancati)
		if (this.ignoraClick || this.rinominaUid === v.uid || v.tipo !== 'menu' || this.ribbonBlocchi) return;
		if (this.verticale) {
			this.apertiV.has(v.uid) ? this.apertiV.delete(v.uid) : this.apertiV.add(v.uid);
		} else {
			this.apertoSub = this.apertoSub === v.uid ? null : v.uid;
			this.riposizionaSub();
		}
	}

	apriChiudi(uid: string) {
		if (this.verticale) {
			this.apertiV.has(uid) ? this.apertiV.delete(uid) : this.apertiV.add(uid);
			return;
		}
		this.iconeDi = null;
		this.apertoSub = null;
		this.aperto = this.aperto === uid ? null : uid;
		this.riposiziona();
	}

	/** Apre un gruppo ovunque sia: un menu della barra o un sottomenu (con il suo menu). */
	private apriGruppo(uid: string) {
		const da = this.svc.trova(uid);
		if (!da || da.nodo.tipo !== 'menu') return;
		if (da.livello === 0) this.apri(uid);
		else this.apriSub(uid);
	}

	private apri(uid: string) {
		if (this.verticale) {
			this.apertiV.add(uid);
		} else if (this.aperto !== uid) {
			this.aperto = uid;
			this.apertoSub = null;
			this.iconeDi = null;
			this.riposiziona();
		}
	}

	private apriSub(uid: string) {
		const padre = this.svc.trova(uid)?.menu;
		if (this.verticale) {
			if (padre) this.apertiV.add(padre.uid);
			this.apertiV.add(uid);
			return;
		}
		if (padre && this.aperto !== padre.uid) this.apri(padre.uid);
		if (this.ribbonBlocchi) return;
		this.apertoSub = uid;
		this.riposizionaSub();
	}

	/** Menu fisso sotto la voce aperta della barra (l'header taglia cio' che esce dal suo riquadro). */
	@HostListener('window:resize')
	riposiziona() {
		if (this.verticale || !this.aperto) return;
		setTimeout(() => {
			const btn = this.host.nativeElement.querySelector(`[data-med-menu-btn="${this.aperto}"]`) as HTMLElement | null;
			if (!btn) return;
			const r = btn.getBoundingClientRect();
			// con la palette aperta (a destra) il menu resta alla sua sinistra
			const destra = window.innerWidth - (this.paletteAperta ? 332 : 0);
			this.posTendina = { top: r.bottom + 6, left: Math.max(8, Math.min(r.left, destra - 336)) };
			this.riposizionaSub();
		});
	}

	/** Sottomenu di lato al menu, all'altezza della sua riga; se a destra non c'e' posto, a sinistra. */
	riposizionaSub() {
		if (this.verticale || !this.apertoSub) return;
		setTimeout(() => {
			const riga = this.host.nativeElement.querySelector(`.med-tendina:not(.med-flyout) [data-med-drag="${this.apertoSub}"]`) as HTMLElement | null;
			const menu = riga?.closest('.med-tendina') as HTMLElement | null;
			if (!riga || !menu) return;
			const r = riga.getBoundingClientRect();
			const m = menu.getBoundingClientRect();
			const destra = window.innerWidth - (this.paletteAperta ? 332 : 0);
			if (this.ribbon) {
				// nella barra come Word il sottogruppo e' una voce in riga: la sua lista si apre sotto, come nel portale
				const sotto = (riga.querySelector(':scope > .med-riga-testa') as HTMLElement | null)?.getBoundingClientRect() || r;
				this.posSub = { top: sotto.bottom + 4, left: Math.max(8, Math.min(sotto.left, destra - 304)) };
				return;
			}
			const left = m.right + 4 + 300 <= destra ? m.right + 4 : Math.max(8, m.left - 304);
			this.posSub = { top: Math.max(8, Math.min(r.top - 7, window.innerHeight - 240)), left };
		});
	}

	nuovoMenu() {
		if (this.ignoraClick) return;
		const menu = this.svc.nuovoGruppo({ menu: null, index: this.svc.albero.length });
		if (!menu) return;
		this.apri(menu.uid);
		this.avviaRinomina(menu);
		setTimeout(() => this.barra?.nativeElement.scrollTo({ left: this.barra.nativeElement.scrollWidth, behavior: 'smooth' }));
	}

	avviaRinomina(n: MenuEdNodo, ev?: Event) {
		ev?.stopPropagation();
		if (n.tipo === 'collegamento') return;
		this.rinominaUid = n.uid;
		this.rinominaIniziale = this.translate.instant(this.svc.titolo(n));
		this.rinominaValore = this.rinominaIniziale;
		setTimeout(() => {
			const input = document.querySelector('app-menu-editor .med-input') as HTMLInputElement | null;
			input?.focus();
			input?.select();
		});
	}

	confermaRinomina() {
		const uid = this.rinominaUid;
		this.rinominaUid = null;
		if (uid && this.rinominaValore !== this.rinominaIniziale) this.svc.rinomina(uid, this.rinominaValore);
	}

	annullaRinomina() {
		this.rinominaUid = null;
	}

	/** Apre la scelta dell'icona della voce, sotto l'icona cliccata. */
	apriIcone(n: MenuEdNodo, ev: Event) {
		ev.stopPropagation();
		if (this.iconeDi?.uid === n.uid) {
			this.iconeDi = null;
			return;
		}
		const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
		const alto = 360;
		const top = r.bottom + 6 + alto > window.innerHeight ? Math.max(8, r.top - 6 - alto) : r.bottom + 6;
		this.iconeDi = { uid: n.uid, top, left: Math.max(8, Math.min(r.left - 8, window.innerWidth - 356)) };
		this.cercaIcona = '';
		setTimeout(() => (document.querySelector('app-menu-editor .med-icone-pop input') as HTMLInputElement | null)?.focus());
	}

	/** Voce di cui si sta scegliendo l'icona. */
	get nodoIcone(): MenuEdNodo | null {
		return this.iconeDi ? this.svc.trova(this.iconeDi.uid)?.nodo || null : null;
	}

	/** Icone filtrate per nome ("casa" non c'e': i nomi sono quelli inglesi di PrimeIcons, es. home, user, chart). */
	iconeFiltrate(): string[] {
		const t = this.cercaIcona
			.trim()
			.toLowerCase()
			.replace(/^pi[ -]?/, '');
		return t ? this.icone.filter((ic) => ic.slice(6).includes(t)) : this.icone;
	}

	scegliIcona(icon: string) {
		if (this.iconeDi) this.svc.cambiaIcona(this.iconeDi.uid, icon);
		this.iconeDi = null;
	}

	/** Gruppo di cui si sta chiedendo come eliminarlo (solo il gruppo o anche le voci) */
	eliminando: string | null = null;

	/** Elimina un gruppo: `conVoci` = anche il contenuto (pagine nascoste, collegamenti tolti), altrimenti le voci restano al suo posto. */
	eliminaGruppo(menu: MenuEdMenu, conVoci = false) {
		this.eliminando = null;
		if (this.aperto === menu.uid) this.aperto = null;
		if (this.apertoSub === menu.uid) this.apertoSub = null;
		this.apertiV.delete(menu.uid);
		if (conVoci) this.svc.eliminaConVoci(menu.uid);
		else this.svc.rimuovi(menu.uid);
		this.riposiziona();
	}

	/** Pannello delle pagine che il menu non fa raggiungere */
	avvisiAperti = false;

	/** Rimette nel menu una pagina non raggiungibile (in fondo al gruppo aperto o alla barra). */
	rimettiPagina(pg_id: number) {
		this.svc.ripristina(pg_id, this.destPalette());
		if (!this.svc.pagineIrraggiungibili().length) this.avvisiAperti = false;
	}

	/** Nomi dei collegamenti persi con una pagina, per la riga dell'avviso. */
	nomiCollegamenti(voci: CompanyMenuShortcut[]): string {
		return voci.map((sc) => this.translate.instant(sc.labelKey)).join(', ');
	}

	/** Mette nel menu (gruppo aperto o barra, in ordine) i collegamenti che si aprivano solo dalla pagina tolta. */
	aggiungiPerse(voci: CompanyMenuShortcut[]) {
		for (const sc of voci) this.svc.aggiungiCollegamento(sc.key, this.destPalette());
	}

	// ---- barra degli strumenti ----

	esci() {
		this.svc.chiediUscita();
	}

	@HostListener('document:keydown', ['$event'])
	tasto(ev: KeyboardEvent) {
		if (ev.key === 'Escape') {
			if (this.trascinamento?.attivo) {
				this.fineTrascinamento();
				this.zone.run(() => (this.bersaglio = null));
			} else if (this.rinominaUid) {
				this.annullaRinomina();
			} else if (this.inseritore) {
				this.inseritore = null;
			} else if (this.iconeDi) {
				this.iconeDi = null;
			} else if (this.apertoSub) {
				this.apertoSub = null;
			} else {
				this.aperto = null;
			}
			return;
		}
		const inInput = (ev.target as HTMLElement)?.closest?.('input, textarea, select, [contenteditable]');
		if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'z' && !inInput && this.svc.puoAnnullare()) {
			ev.preventDefault();
			this.svc.annullaUltima();
		}
	}

	// ---- trascinamento ----

	premi(ev: PointerEvent) {
		if (ev.button !== 0 || this.rinominaUid) return;
		const el = ev.target as HTMLElement;
		// i pulsanti (azioni, "+", strumenti) non trascinano
		if (el.closest('button, input, .med-icone-pop, .p-select, .med-ins-pop')) return;
		const sorgente = el.closest('[data-med-drag], [data-med-nuovo]') as HTMLElement | null;
		if (!sorgente) return;
		// niente selezione del testo della pagina mentre si trascina
		ev.preventDefault();
		this.inseritore = null;
		this.trascinamento = { uid: sorgente.dataset['medDrag'], nuovo: sorgente.dataset['medNuovo'], x: ev.clientX, y: ev.clientY, attivo: false };
		this.zone.runOutsideAngular(() => {
			document.addEventListener('pointermove', this.suMove);
			document.addEventListener('pointerup', this.suUp);
			document.addEventListener('pointercancel', this.suUp);
		});
	}

	private muovi(ev: PointerEvent) {
		const t = this.trascinamento;
		if (!t) return;
		if (!t.attivo) {
			if (Math.abs(ev.clientX - t.x) + Math.abs(ev.clientY - t.y) < 6) return;
			t.attivo = true;
			t.ghost = this.creaGhost(t);
			// cursore e selezione del testo durante il trascinamento (niente regole globali)
			document.body.style.cursor = 'grabbing';
			document.body.style.userSelect = 'none';
			window.getSelection()?.removeAllRanges();
		}
		ev.preventDefault();
		t.ghost!.style.transform = `translate(${ev.clientX + 12}px, ${ev.clientY + 10}px)`;
		this.puntatore = { x: ev.clientX, y: ev.clientY };
		this.scorri(ev.clientX, ev.clientY);
		this.aggiornaBersaglio(ev.clientX, ev.clientY);
	}

	private aggiornaBersaglio(x: number, y: number) {
		const b = this.calcolaBersaglio(x, y);
		const chiave = JSON.stringify(b);
		if (chiave !== this.chiaveBersaglio) {
			this.chiaveBersaglio = chiave;
			this.zone.run(() => (this.bersaglio = b));
			// sosta su un menu o sottomenu chiuso: si apre, come nelle barre dei menu
			const daAprire = b?.tipo === 'dentro' ? b.menu : '';
			if (daAprire !== this.aperturaInAttesa) {
				clearTimeout(this.timerApertura);
				this.aperturaInAttesa = daAprire;
				if (daAprire) this.timerApertura = setTimeout(() => this.zone.run(() => this.apriGruppo(daAprire)), 450);
			}
		}
	}

	private rilascia(ev: PointerEvent) {
		const t = this.trascinamento;
		const b = this.bersaglio;
		const attivo = !!t?.attivo;
		this.fineTrascinamento();
		if (!t || !attivo) return;
		this.ignoraClick = true;
		setTimeout(() => (this.ignoraClick = false));
		this.zone.run(() => {
			this.bersaglio = null;
			if (b && ev.type === 'pointerup') this.applica(t, b);
		});
	}

	private applica(t: { uid?: string; nuovo?: string }, b: Bersaglio) {
		const dest: MenuEdPosizione | null = b.tipo === 'barra' ? { menu: null, index: b.index } : b.tipo === 'riga' ? { menu: b.menu, index: b.index } : b.tipo === 'dentro' ? { menu: b.menu, index: this.gruppo(b.menu)?.voci.length ?? 0 } : null;
		if (t.uid) {
			if (b.tipo === 'cestino') this.svc.trova(t.uid)?.nodo.tipo === 'pagina' ? this.svc.nascondi(t.uid) : this.svc.rimuovi(t.uid);
			else if (dest) this.svc.sposta(t.uid, dest);
		} else if (t.nuovo === 'menu' && dest) {
			const g = this.svc.nuovoGruppo(dest);
			if (g) {
				this.apriGruppo(g.uid);
				this.avviaRinomina(g);
			}
		} else if (t.nuovo?.startsWith('pg:') && dest) {
			this.svc.ripristina(Number(t.nuovo.slice(3)), dest);
		} else if (t.nuovo?.startsWith('sc:') && dest) {
			this.svc.aggiungiCollegamento(t.nuovo.slice(3), dest);
		}
		if (b.tipo === 'dentro') this.apriGruppo(b.menu);
		this.riposiziona();
	}

	private fineTrascinamento() {
		document.removeEventListener('pointermove', this.suMove);
		document.removeEventListener('pointerup', this.suUp);
		document.removeEventListener('pointercancel', this.suUp);
		this.trascinamento?.ghost?.remove();
		this.trascinamento = null;
		this.chiaveBersaglio = '';
		this.aperturaInAttesa = '';
		clearTimeout(this.timerApertura);
		this.fermaScorrimento();
		document.body.style.cursor = '';
		document.body.style.userSelect = '';
	}

	private creaGhost(t: { uid?: string; nuovo?: string }): HTMLElement {
		const sel = t.uid ? `[data-med-drag="${t.uid}"]` : `[data-med-nuovo="${t.nuovo}"]`;
		const sorgente = this.host.nativeElement.querySelector(sel) as HTMLElement | null;
		const ghost = document.createElement('div');
		ghost.className = 'med-ghost';
		ghost.textContent = (sorgente?.innerText || '').split('\n')[0].trim();
		Object.assign(ghost.style, {
			position: 'fixed',
			left: '0',
			top: '0',
			zIndex: '3000',
			pointerEvents: 'none',
			padding: '6px 12px',
			borderRadius: '6px',
			background: 'var(--kcs-surface-card, #fff)',
			color: 'var(--kcs-inputtext-color, #222)',
			border: '1px solid var(--kcs-surface-border, #ccc)',
			boxShadow: '0 6px 18px rgba(0,0,0,.18)',
			fontSize: '13px',
			whiteSpace: 'nowrap',
		});
		document.body.appendChild(ghost);
		return ghost;
	}

	/**
	 * Col puntatore vicino a un bordo scorre, anche da fermo: la barra in alto (piu' larga
	 * dell'header) in orizzontale, la lista sotto il puntatore (menu lungo, sidebar) in verticale.
	 */
	private scorri(x: number, y: number) {
		const passo = 12;
		let scorrimento: { el: HTMLElement; dx: number; dy: number } | null = null;
		const barra = this.barra?.nativeElement;
		if (!this.verticale && barra) {
			const r = barra.getBoundingClientRect();
			const dx = y < r.top - 24 || y > r.bottom + 24 ? 0 : x < r.left + 48 ? -passo : x > r.right - 48 ? passo : 0;
			if (dx) scorrimento = { el: barra, dx, dy: 0 };
		}
		if (!scorrimento) {
			for (let el = document.elementFromPoint(x, y) as HTMLElement | null; el && el !== document.body; el = el.parentElement) {
				const oy = getComputedStyle(el).overflowY;
				if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
					const r = el.getBoundingClientRect();
					const dy = y < r.top + 36 ? -passo : y > r.bottom - 36 ? passo : 0;
					if (dy) scorrimento = { el, dx: 0, dy };
					break;
				}
			}
		}
		this.scorrimento = scorrimento;
		if (scorrimento && !this.timerScorrimento) {
			this.timerScorrimento = setInterval(() => {
				const sc = this.scorrimento;
				if (!sc || !this.trascinamento?.attivo) return this.fermaScorrimento();
				// l'evento scroll (nella zona) riposiziona menu e sottomenu aperti
				sc.el.scrollLeft += sc.dx;
				sc.el.scrollTop += sc.dy;
				// sotto il puntatore fermo ora c'e' un'altra voce
				this.aggiornaBersaglio(this.puntatore.x, this.puntatore.y);
			}, 16);
		} else if (!scorrimento) {
			this.fermaScorrimento();
		}
	}

	private fermaScorrimento() {
		clearInterval(this.timerScorrimento);
		this.timerScorrimento = null;
		this.scorrimento = null;
	}

	private calcolaBersaglio(x: number, y: number): Bersaglio | null {
		const t = this.trascinamento!;
		const el = document.elementFromPoint(x, y) as HTMLElement | null;
		if (!el) return null;
		const trascinato = t.uid ? this.svc.trova(t.uid)?.nodo : null;
		// cosa si trascina, per le regole dei livelli: un gruppo nuovo, una voce nuova o un nodo esistente
		const cosa: MenuEdNodo | 'gruppo' | null = t.nuovo === 'menu' ? 'gruppo' : t.nuovo ? ({ uid: '', tipo: t.nuovo.startsWith('pg:') ? 'pagina' : 'collegamento' } as MenuEdVoce) : trascinato || null;
		if (!cosa) return null;
		const ok = (menu: string | null) => this.svc.puoStare(cosa, menu);

		if (el.closest('[data-med-palette]')) {
			// trascinata sulla palette: il collegamento si toglie, la pagina va tra le nascoste
			return trascinato?.tipo === 'collegamento' || trascinato?.tipo === 'pagina' ? { tipo: 'cestino' } : null;
		}
		const riga = el.closest('[data-med-dd]') as HTMLElement | null;
		if (riga) {
			const menu = riga.dataset['medMenu']!;
			const j = Number(riga.dataset['medDd']);
			const nodo = this.svc.trova(riga.dataset['medDrag']!)?.nodo;
			const testa = (riga.querySelector(':scope > .med-riga-testa') as HTMLElement) || riga;
			const r = testa.getBoundingClientRect();
			// nel pannello a barra come Word le voci stanno in riga: prima/dopo in orizzontale (non nella lista di un sottogruppo)
			const inRiga = this.ribbon && !!riga.closest('.med-ribbon');
			const rel = inRiga ? (x - r.left) / r.width : (y - r.top) / r.height;
			// al centro di un sottomenu: dentro
			if (nodo?.tipo === 'menu' && nodo.uid !== trascinato?.uid && rel > 0.25 && rel < 0.75 && ok(nodo.uid)) return { tipo: 'dentro', menu: nodo.uid };
			if (!ok(menu)) return null;
			const prima = rel < 0.5;
			return { tipo: 'riga', menu, index: j + (prima ? 0 : 1), j, prima };
		}
		const coda = el.closest('[data-med-dd-end], .med-dd-head') as HTMLElement | null;
		if (coda) {
			const menu = coda.dataset['medMenu']!;
			if (!ok(menu)) return null;
			return coda.classList.contains('med-dd-head') ? { tipo: 'riga', menu, index: 0, j: 0, prima: true } : { tipo: 'dentro', menu };
		}
		const voce = el.closest('[data-med-bar]') as HTMLElement | null;
		if (voce) {
			const i = Number(voce.dataset['medBar']);
			const nodo = this.svc.albero[i];
			const btn = (voce.querySelector('.med-btn') as HTMLElement) || voce;
			const r = btn.getBoundingClientRect();
			const rel = this.verticale ? (y - r.top) / r.height : (x - r.left) / r.width;
			if (nodo?.tipo === 'menu' && nodo.uid !== trascinato?.uid && rel > 0.25 && rel < 0.75 && ok(nodo.uid)) return { tipo: 'dentro', menu: nodo.uid };
			const prima = rel < 0.5;
			return { tipo: 'barra', index: i + (prima ? 0 : 1), i, prima };
		}
		if (el.closest('[data-med-bar-end]')) {
			const i = this.svc.albero.length - 1;
			return { tipo: 'barra', index: i + 1, i, prima: false };
		}
		return null;
	}

	insBarra(i: number, prima: boolean): boolean {
		const b = this.bersaglio;
		return b?.tipo === 'barra' && b.i === i && b.prima === prima;
	}

	insRiga(menu: string, j: number, prima: boolean): boolean {
		const b = this.bersaglio;
		return b?.tipo === 'riga' && b.menu === menu && b.j === j && b.prima === prima;
	}

	dentro(menu: string): boolean {
		const b = this.bersaglio;
		return b?.tipo === 'dentro' && b.menu === menu;
	}

	get cestino(): boolean {
		return this.bersaglio?.tipo === 'cestino';
	}

	inTrascinamento(uid: string): boolean {
		return !!this.trascinamento?.attivo && this.trascinamento.uid === uid;
	}
}
